/**
 * Landing-page configuration and the flags that gate unverified content.
 *
 * Nothing in `src/newui/landing/` is imported by app code, and no app component
 * imports from here — keep that boundary so the marketing page can be moved or
 * deleted in one commit.
 */

/**
 * Social proof stays OFF until the slots in DESIGN_PLAN §1.6 are filled with
 * real, permissioned, re-derivable content. Named and specific proof converts;
 * "trusted by thousands" is worse than nothing; and a fabricated quote on a live
 * commercial page is a legal problem, not a placeholder. With this `false` the
 * section does not render and section 5 flows into section 7 with no visible gap.
 *
 * Before flipping to `true` you need, per DESIGN_PLAN §1.6:
 *   - 3 quotes: name + city (or what they sell) + one written sentence, with
 *     permission to publish  → Isaac / support@
 *   - 3 figures, each with the date it was measured  → backend query
 * State the figure AND its date. Round down. Never write a number you cannot
 * re-derive from a query.
 */
export const SHOW_SOCIAL_PROOF = false;

/**
 * One objective: start a trial. Every primary CTA on the page carries the same
 * label and the same destination. `/login` renders both sign-in and sign-up;
 * `?mode=signup` opens it on the sign-up form so a trial visitor is never shown
 * "Welcome back" first (AuthScreen honours that query value explicitly).
 *
 * Do NOT link to `/billing`: it is behind auth and an unsubscribed visitor
 * would be bounced.
 */
export const PRIMARY_CTA = { label: 'Start free trial', to: '/login?mode=signup' } as const;
export const SECONDARY_CTA = { label: 'Sign in', to: '/login' } as const;

/**
 * Trial length, mirrored from `TRIAL_PERIOD_DAYS` in oneClickTixCore
 * `flashtools/services/billing.py`. Only Core is trial-eligible
 * (`TRIAL_ELIGIBLE_TIERS`) — Pro bills from its first invoice — which is why
 * every string below says "on Core" rather than implying a trial on both plans.
 * Change this in the same commit as the server constant.
 */
export const TRIAL_DAYS = 3;

/**
 * Sits under every primary CTA. It names the trial length, the plan it applies
 * to, and the fact that a card is taken at signup, because Checkout collects one
 * (`payment_method_collection: 'always'`) and Stripe charges it automatically
 * the moment the trial ends. Visitors who only learned that at the charge read
 * it as a surprise bill; saying it here is cheaper than a refund.
 */
export const CTA_MICROCOPY =
  `${TRIAL_DAYS}-day free trial on Core, then $30/month. Card required. Cancel anytime.`;

/** Shared layout strings so every section lines up on the same grid. */
export const CONTAINER = 'mx-auto w-full max-w-[1200px] px-6 lg:px-8';
export const SECTION_PADDING = 'py-20 lg:py-28';

export const ANCHORS = {
  features: 'features',
  how: 'how',
  plans: 'plans',
} as const;
